export enum ETaskType {
    GET_FIRST_PROSPECT = 'GET_FIRST_PROSPECT',
    ACQUIRE_PROSPECT = 'ACQUIRE_PROSPECT',
    MAINTAIN_AVG_RATING = 'MAINTAIN_AVG_RATING',
    EARN_5_STAR_REVIEWS = 'EARN_5_STAR_REVIEWS',
    LIST_ALL_RELEVANT_CHANNEL = 'LIST_ALL_RELEVANT_CHANNEL',
    TURN_ON_AVA_AUTO_REPLY = 'TURN_ON_AVA_AUTO_REPLY',
    UPLOAD_NEW_LICENSE = 'UPLOAD_NEW_LICENSE',
    GET_FIRST_APPOINTMENT = 'GET_FIRST_APPOINTMENT',
    ACQUIRE_PROSPECT_5 = 'ACQUIRE_PROSPECT_5',
    ACQUIRE_PROSPECT_25 = 'ACQUIRE_PROSPECT_25',
    ACQUIRE_PROSPECT_50 = 'ACQUIRE_PROSPECT_50',
    ACQUIRE_PROSPECT_100 = 'ACQUIRE_PROSPECT_100',
    ACQUIRE_PROSPECT_150 = 'ACQUIRE_PROSPECT_150',
}

export type TTaskItem = {
    id: number;
    parentTaskId: number;
    taskCode: ETaskType;
    taskName: string;
    stageId: number;
    description: string;
    type: string;
    taskCycle: null;
    taskThreshold: number;
    subTaskExecType: null;
    kiwiUser: boolean;
    klarityUser: boolean;
    status: 'A' | 'D';
    effectTime: null;
    expireTime: null;
    level: number;
    priority: number;
    createAt: string;
    lastUpdateAt: string;
    createBy: null;
    lastUpdateBy: null;
    completeThreshold: null;
    completed: false;
};

export type TTaskList = (TTaskItem & {
    childrenTaskList: TTaskItem[];
})[];

export type TTaskStage = {
    id: number;
    name: string;
    stageCode: string;
    priority: 1;
    createAt: string;
    lastUpdateAt: string;
    createBy: null;
    lastUpdateBy: null;
};

export type TTaskData = {
    taskStage: TTaskStage;
    taskList: TTaskList;
};

export type TProfileData = {
    googleProfileLink: string;
    klarityProfileLink: string;
    listedChannelCount: number;
    pfLink: string;
    notCompetitiveServicePer: null | number;
    waitReplyReviewCount: null | number;
    referralLink?: string;
};

// 预约地址类型
export type TAppointmentAddress = {
    id: number;
    providerId: number;
    practiceAddress: string;
    practiceCity: string;
    practiceState: string;
    practiceZip: string;
    publicType: string;
    isDefault: boolean;
    asMailing: null | boolean;
};

// 即将到来的预约项类型
export type TUpcomingAppointment = {
    id: number;
    providerId: number;
    appointmentName: string;
    clientId: number;
    startAt: string;
    endAt: string;
    duration: number;
    actualDuration: number;
    contactType: string;
    appointmentStatus: string;
    appointmentStatusChangedAt: null | string;
    providerConfirm: boolean;
    providerConfirmAt: null | string;
    clientConfirm: boolean;
    clientConfirmAt: null | string;
    externalVideoLink: string;
    notes: string;
    reason: string;
    serviceType: string;
    serviceSpecialty: string;
    serviceState: string;
    serviceId: number;
    location: null | string;
    telephone: string;
    createdAt: string;
    billingList: null | unknown;
    clientName: string;
    address: TAppointmentAddress[];
};

// 获取即将到来的预约请求参数
export type TUpcomingAppointmentRequest = {
    start: string;
    end: string;
};

// 获取即将到来的预约响应类型
export type TUpcomingAppointmentResponse = {
    pageNo: number;
    pageSize: number;
    totalRecords: number;
    isLast: boolean;
    list: TUpcomingAppointment[];
};

// 未读消息记录类型（与TConversation类似，但用于API响应）
export type TUnreadMessageRecord = {
    conversationId: string;
    clientId: number;
    providerId: number;
    lastMessageId: string;
    lastMessageContent: string;
    lastMessageType: string;
    lastMessageAt: string;
    lastMessageSenderType: string;
    lastMessageSenderId: number;
    unreadCount: number;
    status: string;
    pinned: boolean;
    pinTime: string | null;
    lastReadAt: string | null;
    createdAt: string;
    patientFirstName: string;
    patientLastName: string;
    providerFirstName: string;
    providerLastName: string;
    providerAvatarUrl: string;
};

// 未读消息分页信息类型
export type TUnreadMessagesPage = {
    records: TUnreadMessageRecord[];
    total: number;
    size: number;
    current: number;
    orders: unknown[];
    optimizeCountSql: boolean;
    searchCount: boolean;
    countId: string | null;
    maxLimit: number | null;
    pages: number;
};

// 获取未读消息响应类型
export type TUnreadMessagesResponse = {
    unReadCount: number;
    unReadMessages: TUnreadMessagesPage;
};

// 预约趋势请求参数
export type TAppointmentTrendRequest = {
    start: string;
    end: string;
};

// 预约趋势数据项
export type TAppointmentTrendItem = {
    id: number | null;
    providerId: number | null;
    metricDate: string; // 日期，格式：YYYY-MM-DD
    state: string | null;
    specialty: string | null;
    bookingsCount: number; // 预约数量
    adBookingsCount: number; // 广告预约数量
    totalSpendCents: number; // 总花费（分）
    impressionsCount: number; // 展示次数
    auctionParticipantsCount: number; // 拍卖参与者数量
    createdAt: string | null;
    updatedAt: string | null;
};

// 预约趋势响应类型
export type TAppointmentTrendResponse = TAppointmentTrendItem[];

// 获取未来N小时可用时间响应类型
export type TAvailableHoursResponse = {
    code: number;
    message: string;
    data: number; // 可用小时数
};

// 收益计算结果类型
export type TEarningsCalculationResult = {
    totalEstimatedEarnings: number;
    estimatedMonthlyTotalIncome: number;
};

// 广告数据指标摘要类型
export type TAdDataMetricSummary = {
    joinedAt: string | null;
    totalBookings: number;
    adBookings: number;
    previousAdBookings: number;
    totalImpressions: number;
    totalSpendCents: number;
    auctionParticipants: number;
    previousAuctionParticipants: number;
    previousTotalBookings: number;
    totalBookingsRate: number;
    previousTotalImpressions: number;
    totalImpressionsRate: number;
    trends: unknown | null;
    pauseTimeRanges: unknown | null;
};

// Performance 接口响应类型
export type TPerformanceResponse = {
    code: number;
    message: string;
    data: {
        earningsCalculationResult: TEarningsCalculationResult;
        adDataMetricSummary: TAdDataMetricSummary;
    };
};

// Home To-do Counts 响应类型（已废弃，使用 TreatmentPlanAppointmentCountsVo）
export type THomeTodoCountsVo = {
    textInProgressCount: number; // 进行中的文本访问数量 (status=ACTIVE, treatmentSubmitted=false)
    unsignedTreatmentPlanCount: number; // 未签名的治疗计划数量 (statuses IN_PROGRESS + OVERDUE)
    totalCount: number; // 总待办数量 (textInProgressCount + unsignedTreatmentPlanCount)
};

export type THomeTodoCountsResponse = {
    code: number;
    message: string;
    data: THomeTodoCountsVo;
};

// 治疗计划预约计数响应类型（新接口）
export type TreatmentPlanAppointmentCountsVo = {
    dismissedCount: number; // Dismissed count
    inProgressCount: number; // In-progress count (submitted within 0-3 days) - 包括 Appointments 和 Text Visits
    overdueCount: number; // Overdue count (submitted > 3 days ago) - 仅 Appointments
    sentCount: number; // Sent count (shared with patient) - 包括 Appointments 和 Text Visits
};

export type TreatmentPlanAppointmentCountsResponse = {
    code: number;
    message: string;
    data: TreatmentPlanAppointmentCountsVo;
};

// 治疗计划预约查询请求参数
export type TTreatmentPlanAppointmentQueryReq = {
    clientId?: number; // 客户端ID（患者ID），可选
    current?: number; // 当前页码，从1开始
    size?: number; // 每页大小
    orderKey?:
        | 'CREATE_AT'
        | 'LAST_ACTION_AT'
        | 'CONTRACT_VERSION'
        | 'LAST_UPDATE_AT'
        | 'UPDATE_AT'
        | 'AMOUNT'
        | 'CHARGE_SUCCEED_AT'
        | 'SORT'
        | 'START_AT'; // 排序字段
    orderType?: 'ASC' | 'DESC'; // 排序类型
    status?: 'IN_PROGRESS' | 'OVERDUE' | 'SENT' | 'DISMISSED' | 'UPCOMING'; // 治疗计划状态筛选
    // true = 只返回 provider appointment，过滤 text visit（claim 只由 appointment 产生）；
    // 不传/false 行为一致，Home To-do list 不受影响
    appointmentOnly?: boolean;
};

// 治疗计划预约项（基于 Swagger 的 ProviderAppointmentVo）
export type TTreatmentPlanAppointmentItem = {
    id: number;
    providerId: number;
    clientId: number;
    appointmentName: string;
    startAt: string;
    endAt: string;
    duration: number;
    actualDuration?: number;
    contactType: string;
    appointmentStatus: string;
    appointmentStatusChangedAt?: string | null;
    providerConfirm: boolean;
    providerConfirmAt?: string | null;
    clientConfirm: boolean;
    clientConfirmAt?: string | null;
    externalVideoLink?: string;
    notes?: string;
    reason?: string;
    scheduledBy?: string;
    serviceType: string;
    serviceSpecialty: string;
    serviceState: string;
    serviceId: number;
    location?: string | null;
    telephone?: string;
    isSample?: boolean;
    createdAt: string;
    updateAt: string;
    calendarColor?: string;
    recurringAppointmentId?: number;
    emailSignature?: string;
    providerAppointmentCancelInfo?: unknown;
    providerAppointmentBillings?: unknown[];
    hasTextVisitTreatment?: boolean;
    textVisitTreatmentVo?: unknown | null;
    attachmentCount?: number;
    prescriptions?: unknown | null;
    providerFirstName?: string | null;
    providerLastName?: string | null;
    providerCredential?: string | null;
    providerPhoto?: string | null;
    providerTreatmentSubmitted?: boolean;
};

// 分页响应
export type TPageResponse<T> = {
    list: T[];
    totalRecords: number;
    pageSize: number;
    pageNo: number;
    isLast: boolean;
};

// 治疗计划预约列表响应
export type TTreatmentPlanAppointmentListResponse = {
    code: number;
    message: string;
    data: TPageResponse<TTreatmentPlanAppointmentItem>;
};

// Text Visit 治疗方案VO
export type TextVisitTreatmentVo = {
    id: number;
    businessId: number;
    businessType: string;
    providerId: number;
    clientId: number;
    treatmentPlan: string;
    aiTreatmentPlan?: string | null;
    aiTreatmentDraftStatus: boolean;
    autoFollowUp: boolean;
    autoFollowUpInterval: number;
    autoFollowUpIntervalUnit: string;
    autoFollowUpMessage: string;
    providerNote: string;
    createdAt: string;
    updatedAt: string;
    attachments?: unknown | null;
    prescriptions?: unknown | null;
};

// 治疗计划项VO（聚合了Appointments和Text Visits）
export type TreatmentPlanItemVo = {
    itemType: 'APPOINTMENT' | 'TEXT_VISIT' | 'STANDALONE';
    appointment?: TTreatmentPlanAppointmentItem | null;
    textVisit?: {
        id: number;
        textVisitUuid: string;
        billingId?: number | null;
        klarityAssessmentUuid: string;
        assessmentName?: string | null;
        klarityUserId: number;
        clientId: number;
        aiSummary?: string;
        aiSummaryContextMd5?: string;
        aiSummaryGenerateAt?: string;
        aiTags?: string;
        serviceType: string;
        specialty?: string;
        patientAdditionalComment?: string;
        status: string;
        conditions: string;
        state: string;
        patientSubmitAt?: string;
        assignedProviderId?: number;
        assignedProviderAt?: string;
        providerTreatmentSubmittedAt?: string | null;
        providerTreatmentSubmitted: boolean;
        providerUnreadCount?: number;
        patientUnreadCount?: number;
        source?: string;
        closedAt?: string | null;
        cancelledAt?: string | null;
        cancelledBy?: string | null;
        relatedTextVisitId?: number | null;
        createdAt: string;
        updatedAt: string;
        hasTextVisitTreatment?: boolean;
        textVisitTreatmentVo?: TextVisitTreatmentVo | null;
        attachmentCount?: number;
        prescriptions?: unknown | null;
    } | null;
    standaloneNote?: {
        id: number;
        businessId: number;
        clientId: number;
        providerId: number;
        specialty?: string | null;
        treatmentSubmittedAt?: string | null;
        treatmentPlan?: string;
        providerNote?: string;
        attachmentCount?: number;
        createdAt: string;
        updatedAt: string;
    } | null;
    sortCreatedAt: string;
    sortStartAt: string;
    sortUpdatedAt: string | null;
    provider?: {
        avatar: string;
        firstName: string;
        lastName: string;
        providerId: number;
    };
};

// 治疗计划项列表响应（后端分页）
export type TreatmentPlanItemListResponse = {
    code: number;
    message: string;
    data: TPageResponse<TreatmentPlanItemVo>;
};
